import { cssJoin } from '@volvo-cars/css/utils';
import type React from 'react';
import { forwardRef, useMemo, useState } from 'react';
import { TextLikeInput } from './text-input';
import type { TextLikeProps } from './types';
import { UnitDisplay } from './unit-display';
import { useIds } from './use-ids';

export type UnitInputProps = Omit<
  TextLikeProps,
  'autoCapitalize' | 'autoComplete' | 'autoCorrect' | 'spellCheck' | 'variant'
> & {
  unit:
    | 'bit'
    | 'byte'
    | 'celsius'
    | 'centimeter'
    | 'day'
    | 'degree'
    | 'fahrenheit'
    | 'fluid-ounce'
    | 'foot'
    | 'gallon'
    | 'gigabit'
    | 'gigabyte'
    | 'gram'
    | 'hour'
    | 'inch'
    | 'kilobit'
    | 'kilobyte'
    | 'kilogram'
    | 'kilometer'
    | 'liter'
    | 'megabit'
    | 'megabyte'
    | 'meter'
    | 'microsecond'
    | 'mile'
    | 'mile-scandinavian'
    | 'milliliter'
    | 'millimeter'
    | 'millisecond'
    | 'minute'
    | 'month'
    | 'nanosecond'
    | 'ounce'
    | 'percent'
    | 'petabyte'
    | 'pound'
    | 'second'
    | 'stone'
    | 'terabit'
    | 'terabyte'
    | 'week'
    | 'yard'
    | 'year';

  /**
   * Locale unit translation.
   *
   * Defaults to the value of `<html lang>`.
   */
  locale?: string;

  /**
   * How to display the unit.
   *
   * @default 'short'
   */
  unitDisplay?: 'short' | 'long';

  /**
   * Aligns the input content.
   */
  align?: 'start' | 'end';

  /**
   * Specifies the visible width, in characters, of the input.
   */
  size?: number;

  /**
   * Value of the input.
   *
   * Makes the input controlled.
   */
  value?: number | string;

  /**
   * Default value of an uncontrolled input as a locale formatted string.
   */
  defaultValue?: number | string;

  /**
   * Fires immediately when the input’s value is changed by the user (for example, it fires on every keystroke).
   */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;

  /**
   * Id of a `<datalist>` element with a list of predefined values to suggest to the user.
   */
  list?: string;

  /**
   * Hint the browser about the appropriate virtual keyboard for the type of expected data.
   *
   * Use `text` if you need support for negative numbers as the minus sign is not available on iPhone keyboards.
   *
   * @default 'numeric'
   */
  inputMode?: 'text' | 'numeric' | 'decimal';
};

/**
 * The `UnitInput` component displays a localised unit as a suffix to an input field.
 */
export const UnitInput = forwardRef<HTMLInputElement, UnitInputProps>(
  function UnitInput(
    {
      id,
      locale = typeof document !== 'undefined'
        ? document.documentElement.lang
        : 'en',
      align,
      unit,
      unitDisplay = 'short',
      inputMode = 'numeric',
      value,
      defaultValue,
      ...props
    }: UnitInputProps,
    ref: React.ForwardedRef<HTMLInputElement>,
  ) {
    const { rootId, suffixId, prefixId } = useIds(id);
    const formatter = useMemo(
      () =>
        new UnitDisplay({
          locale,
          unit,
          unitDisplay,
        }),
      [locale, unit, unitDisplay],
    );

    const contentAfter = formatter.suffix ? (
      <span className="text-secondary whitespace-nowrap" id={suffixId}>
        {formatter.suffix}
      </span>
    ) : null;

    return (
      <TextLikeInput
        {...props}
        ref={ref}
        inputMode={inputMode}
        type="text"
        variant="top-label"
        autoComplete="off"
        autoCapitalize="none"
        spellCheck={false}
        id={rootId}
        // Pass aria-labelledby to ensure screen readers read the currency symbols
        aria-labelledby={[
          formatter.prefix && prefixId,
          formatter.suffix && suffixId,
        ]
          .filter(Boolean)
          .join(' ')}
        contentAfter={contentAfter}
        inputClassName={cssJoin(align === 'end' && 'text-end')}
        value={value?.toString()}
        defaultValue={defaultValue?.toString()}
      />
    );
  },
);
